import {useState, ReactNode} from 'react'
import {useParams} from 'react-router-dom'
import {parseISO, getMonth, getYear, differenceInCalendarMonths} from 'date-fns'
import {v4 as uuidv4} from 'uuid'
import cn from 'clsx'
import {Space} from 'antd'
import useStore from 'base/store'
import {PermissionType} from 'common/types'
import styles from './ProjectForecastList.module.scss'
import {useOpen} from 'common/hooks'
import {
  ListHeader,
  Box,
  Block,
  Circle,
  Text,
  VerticalDivider,
  Button,
  Modal,
  UserAvatar,
  IconButton,
  RotateArrowFourthIcon,
  BlackboardChartSecondIcon,
  ArrowToIcon,
  CentsMoney,
  PinIcon,
} from 'common/components'
import {useGetForecast} from 'common/api'
import {
  countPercentage,
  formatDate,
  formatMoney,
  isSameYearMonth,
  maybePluralize,
  totalNumberFromArray,
  arrayFromMonthsCount,
  fixNumber,
  isItemDrawdown,
  isItemRepayment,
  isEstimatedForecastItem,
  isTransactedForecastItem,
} from 'common/helpers'
import {
  ForecastItem as ForecastItemInterface,
  ForecastFormItem,
} from 'common/types'
import {ForecastHistory, ForecastItemForm, useGetProject} from '..'

interface ListProps {
  months: number
  openDate: Date
  forecast: ForecastItemInterface[]
}

interface ItemProps {
  item: ForecastItemInterface
  index: number
  listCount: ListCount
  fullMonthsForecast: ForecastItemInterface[]
}

interface ForecastItemProps {
  item: ForecastItemInterface
  number: number
  prevItemDate: Date
  nextItemDate: Date
}

interface EmptyItemProps {
  showBottomBorder: boolean
  date: Date
}

interface BarBoxProps {
  right?: boolean
  label: string
  value: ReactNode
  subLabel?: string
}

interface BarProps {
  months: number
  openDate: Date
  project: any
  forecast: ForecastItemInterface[]
}

interface RepaymentsBarProps {
  forecast: ForecastItemInterface[]
  totalAmount: number
}

interface AddForecastProps {
  projectUuid: string
  openDate: Date
  loanValue: number
  forecast: ForecastItemInterface[]
}

interface ListCount {
  drawdown: number
  repayment: number
}

export default function ProjectForecastList() {
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const params = useParams()
  const {data: project} = useGetProject(params.uuid)
  const {data: forecast} = useGetForecast(params.uuid)

  // console.log(forecast)
  // console.table(forecast?.map((x:any, index: number) => ({
  //   date: x?.date?.split('T')?.[0],
  //   dateV: x?.value?.date?.split('T')?.[0],
  //   amount: x?.value?.amount || 0,
  //   type: x?.type,
  //   initinitialDrawdown: x?.initialDrawdown,
  //   // dd: x?.drawdown,
  //   ddReqAmount: x?.drawdown?.requestedAmount,
  //   order: index + 1,
  // })))

  const openDate = parseISO(project.openDate)
  const lastDate = parseISO(forecast.at(-1).value.date)
  const months = differenceInCalendarMonths(lastDate, openDate)
  const notClosed = project.status !== 'closed'

  return (
    <>
      <Head />
      <List months={months + 1} openDate={openDate} forecast={forecast} />
      {notClosed && !!permissions?.includes('edit forecast') && (
        <AddForecast
          projectUuid={project.uuid}
          openDate={openDate}
          loanValue={project.totalAmount}
          forecast={forecast}
        />
      )}
      <Bar
        months={months}
        openDate={openDate}
        project={project}
        forecast={forecast}
      />
    </>
  )
}

const Head = () => (
  <ListHeader className={cn(styles.grid, styles.grid_head)}>
    <Box align="right">Month</Box>
    <div>Forecast type</div>
    <Box align="center">LM</Box>
    <Box align="center">Live forecast</Box>
    <div>Date</div>
    <div>Debit / Credit</div>
    <Box align="center">Forecast for KPI</Box>
    <Box align="right">% var.</Box>
    <Box align="center">KPI</Box>
    <Box align="right">History</Box>
  </ListHeader>
)

const List = ({months, openDate, forecast}: ListProps) => {
  const listCount: ListCount = {
    drawdown: 0,
    repayment: 1,
  }
  const monthsArray = arrayFromMonthsCount(months, openDate)
  const fullMonthsForecast: ForecastItemInterface[] = []
  monthsArray.forEach((date) => {
    forecast.some((item) => isSameYearMonth(item.value.date, date))
      ? fullMonthsForecast.push(
          ...forecast.filter((item) => isSameYearMonth(item.value.date, date))
        )
      : fullMonthsForecast.push({
          uuid: uuidv4(),
          date: date.toISOString(),
          loanRequest: null,
          isFake: false,
          createdAt: null,
          rating: null,
          transactions: [],
          type: null,
          updatedAt: null,
          value: null,
          valueForRating: null,
        })
  })

  return (
    <ul className={styles.list}>
      {fullMonthsForecast.map((item, index) => (
        <Item
          key={item.uuid}
          item={item}
          index={index}
          listCount={listCount}
          fullMonthsForecast={fullMonthsForecast}
        />
      ))}
    </ul>
  )
}

const Item = ({item, index, listCount, fullMonthsForecast}: ItemProps) => {
  const lastItemIndex = fullMonthsForecast.length - 1
  const prevItem = index === 0 ? null : fullMonthsForecast[index - 1]
  const nextItem =
    index === lastItemIndex ? null : fullMonthsForecast[index + 1]

  if (!item.type)
    return (
      <EmptyItem
        key={item.uuid}
        showBottomBorder={
          index === lastItemIndex ||
          !fullMonthsForecast[index + 1].transactions?.length
        }
        date={parseISO(item.date)}
      />
    )

  return (
    <ForecastItem
      key={item.uuid}
      item={item}
      number={
        isItemDrawdown(item) ? listCount.drawdown++ : listCount.repayment++
      }
      prevItemDate={
        prevItem ? parseISO(prevItem.value?.date ?? prevItem.date) : null
      }
      nextItemDate={
        nextItem ? parseISO(nextItem.value?.date ?? nextItem.date) : null
      }
    />
  )
}

const EmptyItem = ({showBottomBorder, date}: EmptyItemProps) => {
  return (
    <Block
      as="li"
      className={cn(styles.grid, showBottomBorder && styles.grid_bottomBorder)}
      type="transparent"
    >
      <Box className={styles.block} align="right">
        <Circle border color="grey" />
        <Text weight="demiBold">{formatDate(date, 'fourth')}</Text>
      </Box>
    </Block>
  )
}

const ForecastItem = ({
  item,
  number,
  prevItemDate,
  nextItemDate,
}: ForecastItemProps) => {
  const permissions: PermissionType[] = useStore((state) => state.permissions)

  const [isOpen, handleOpen, handleClose] = useOpen()

  const {createdBy} = item.value
  const estimated = isEstimatedForecastItem(item)
  const isRepayment = isItemRepayment(item)
  const isInitialDrawdown = !isRepayment && number === 0
  const date = parseISO(item.value.date)
  const showBottomBorder =
    estimated &&
    (getYear(nextItemDate) !== getYear(date) ||
      getMonth(nextItemDate) !== getMonth(date))
  const title = isInitialDrawdown
    ? 'Initial drawdown'
    : `${estimated ? 'estimated ' : item.isFake ? 'imported ' : ''}${
        item.type
      } ${number}`
  const modalTitle = `${title} forecast history`

  return (
    <>
      <Block
        as="li"
        className={cn(
          styles.grid,
          showBottomBorder && styles.grid_bottomBorder
        )}
        type={estimated ? 'transparent' : 'grey'}
        onClick={isInitialDrawdown ? undefined : handleOpen}
      >
        <Box className={styles.block} align="right">
          <Circle
            border
            color={
              estimated ? 'turquoize' : isRepayment ? 'darkGreen' : 'darkGrey'
            }
          />
          {!isSameYearMonth(prevItemDate, date) && (
            <Text weight="demiBold">{formatDate(date, 'fourth')}</Text>
          )}
        </Box>
        <Space>
          {isRepayment && <RotateArrowFourthIcon />}
          <p className={styles.type}>{title}</p>
        </Space>
        <Box align="center">
          <UserAvatar
            src={createdBy.photo}
            size={26}
            alt={createdBy.firstName}
          />
        </Box>
        <div className={styles.block}>
          <Text size="xs" color="grey">
            {formatDate(item.updatedAt, 'seventh')}
          </Text>
          <Text
            align="right"
            weight={estimated ? 'bold' : undefined}
            color={estimated ? 'turquoizeBright' : undefined}
          >
            {formatMoney(item.value.amount, {
              prefix: false,
              cents: true,
              parentheses: isRepayment,
            })}
          </Text>
        </div>
        <Box className={styles.date}>
          <VerticalDivider className={styles.divider} margin="small" />
          <Text color={estimated ? 'turquoizeDeep' : undefined}>
            {formatDate(date, 'fifth')}
          </Text>
        </Box>
        {estimated ? (
          <>
            <div />
            <div />
            <div />
            <div />
          </>
        ) : (
          <>
            <Text weight="demiBold" align="left">
              {formatMoney(
                item.transactions?.length
                  ? totalNumberFromArray(item.transactions, 'amount')
                  : item.value.amount,
                {
                  prefix: false,
                  cents: true,
                }
              )}
            </Text>
            <div className={styles.block}>
              <Text size="xs" color="grey">
                {item.valueForRating && formatDate(item.valueForRating.createdAt, 'seventh')}
              </Text>
              <Text align="right">
                {item.valueForRating && formatMoney(item.valueForRating.amount, {
                  prefix: false,
                  cents: true,
                })}
              </Text>
            </div>
            {item.transactions?.length ? (
              <>
                <Text align="right" color="grey">
                  {item.rating && `${item.rating?.variance}%`}
                </Text>
                <Text align="center" weight="demiBold">
                  {item.rating?.points}
                </Text>
              </>
            ) : (
              <>
                <div />
                <div />
              </>
            )}
          </>
        )}
        { !!permissions?.includes('edit forecast') &&
          <Box color="brown">{!isInitialDrawdown && <ArrowToIcon />}</Box>
        }
      </Block>
      <Modal
        title={
          <Space>
            <RotateArrowFourthIcon width={20} height={20} />
            <span>{modalTitle}</span>
          </Space>
        }
        width={692}
        open={isOpen}
        onCancel={handleClose}
      >
        <ForecastHistory
          isRepayment={isRepayment}
          item={item}
          number={number}
        />
      </Modal>
    </>
  )
}

const AddForecast = ({
  projectUuid,
  openDate,
  loanValue,
  forecast,
}: AddForecastProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()

  const currentForecast: ForecastFormItem[] = forecast.map((item: any) => ({
    uuid: item.uuid,
    type: item.type,
    date: parseISO(item.value.date),
    amount: item.transactions?.length
      ? totalNumberFromArray(item.transactions, 'amount')
      : item.value.amount,
    transactions: Boolean(item.transactions?.length || item.isFake),
  }))

  return (
    <>
      <Box align="center" mb={32}>
        <Button variant="secondary" onClick={handleOpen}>
          add forecast
        </Button>
      </Box>
      <Modal
        title="Add forecast item"
        width={486}
        open={isOpen}
        onCancel={handleClose}
      >
        <ForecastItemForm
          projectUuid={projectUuid}
          minDate={openDate}
          loanValue={loanValue}
          forecast={currentForecast}
          onClose={handleClose}
        />
      </Modal>
    </>
  )
}

const Bar = ({months, openDate, project, forecast}: BarProps) => {
  const [isSticky, setSticky] = useState(false)

  const handleStickyToggle = () => setSticky((state) => !state)
  // forecasted withdrawn
  // api获取forcast, 将有transactions的item 或 没有transactions但isFake=true的过滤出来
  // 二次过滤：将valueForRating有内容的过滤出来
  // 将过滤出来的数据，计算amount的总和
  const transactions = forecast.filter((item) => isTransactedForecastItem(item))
  // actual loan withdrawn:
  // api获取出的focast, 过滤出type中包含drawdown字符串的item
  // 二次过滤：（item.transactions?.length || item.isFake） 为真的过滤出来
  // 遍历过滤出来的数组，如果transactions.length > 0，则计算transactions item的amount总和，否则取item.value
  // 最后再计算所有数据总和
  const drawdowns = forecast.filter((item) => isItemDrawdown(item))
  const transactedNoFakeDrawdowns = drawdowns.filter(
    (item) => item.transactions?.length
  )
  const transactedDrawdowns = drawdowns.filter((item) =>
    isTransactedForecastItem(item)
  )
  const totalEstimatedDrawdownsAmount = totalNumberFromArray(
    drawdowns
      .filter((item) => isEstimatedForecastItem(item))
      .map((item) => item.value),
    'amount'
  )
  const availableEstimatedWithdrawalAmount =
    project.totalAmount - totalEstimatedDrawdownsAmount

  const totalTransactedDrawdownsAmount = totalNumberFromArray(
    transactedDrawdowns.map((item) =>
      item.transactions?.length
        ? totalNumberFromArray(item.transactions, 'amount')
        : item.value
    )
  )
  const firstEstimatedForecast = forecast.find(
    (item) => item.transactions?.length
  )
  const transactedMonths = firstEstimatedForecast
    ? differenceInCalendarMonths(
        parseISO(firstEstimatedForecast.value.date),
        openDate
      )
    : 0
  const totalEstimatedRatingDrawdownsAmount = totalNumberFromArray(
    transactions.map((item) => item.valueForRating).filter(Boolean),
    'amount'
  )
  const averageVariance =
    totalNumberFromArray(
      transactions.filter((item) => item.rating).map((item) => item.rating),
      'variance'
    ) / transactions.length || 0
  const averagePoints =
    totalNumberFromArray(
      transactions.filter((item) => item.rating).map((item) => item.rating),
      'points'
    ) / transactions.length || 0

  const hasRepayments = forecast.some((item) => isItemRepayment(item))

  return (
    <Block
      className={cn(styles.bar, isSticky && styles.bar_sticky)}
      type="black"
    >
      <IconButton
        className={styles.pin}
        icon={<PinIcon />}
        onClick={handleStickyToggle}
      />
      <div className={cn(styles.grid, styles.grid_bar)}>
        <BarBox
          label="period"
          value={maybePluralize(transactedMonths, 'mnth')}
          subLabel={maybePluralize(months, 'month')}
        />
        <BarBox
          label="drawdowns"
          value={maybePluralize(transactedNoFakeDrawdowns.length, 'drawdown')}
          subLabel={maybePluralize(drawdowns.length, 'drawdown')}
        />
        <BarBox
          right
          label="estimated drawdowns"
          value={
            <Box size="xl" color="turquoizeBright">
              <Space size={6}>
                <BlackboardChartSecondIcon width={16} height={16} />
                <CentsMoney value={totalEstimatedDrawdownsAmount} />
              </Space>
            </Box>
          }
          subLabel={`${formatMoney(availableEstimatedWithdrawalAmount, {
            cents: true,
          })} available`}
        />
        <BarBox
          right
          label="actual loan withdrawn"
          value={formatMoney(totalTransactedDrawdownsAmount, {cents: true})}
          subLabel={`${countPercentage(
            totalTransactedDrawdownsAmount,
            project.totalAmount
          )}% from loan`}
        />
        <BarBox
          right
          label="forecasted withdrawn"
          value={formatMoney(totalEstimatedRatingDrawdownsAmount, {
            cents: true,
          })}
          subLabel={`forecasted ${countPercentage(
            totalEstimatedRatingDrawdownsAmount,
            project.totalAmount
          )}% loan`}
        />
        <Box className={styles.barBlock} align="right">
          <div>
            <p className={styles.label}>variance</p>
            <Text size="xl" color="grey" weight="demiBold">
              {fixNumber(averageVariance)}%
            </Text>
          </div>
          <div>
            <p className={styles.label}>points</p>
            <p className={styles.value}>{fixNumber(averagePoints)}</p>
          </div>
        </Box>
      </div>
      {hasRepayments && (
        <RepaymentsBar forecast={forecast} totalAmount={project.totalAmount} />
      )}
    </Block>
  )
}

const RepaymentsBar = ({forecast, totalAmount}: RepaymentsBarProps) => {
  const repayments = forecast.filter((item) => isItemRepayment(item))
  const transactedRepayments = repayments.filter(
    (item) => item.transactions?.length
  )
  const totalEstimatedRepaymentsAmount = totalNumberFromArray(
    repayments
      .filter((item) => !item.transactions?.length)
      .map((item) => item.value),
    'amount'
  )
  const totalTransactedRepaymentsAmount = totalNumberFromArray(
    transactedRepayments.map((item) => item.transactions?.length),
    'amount'
  )

  return (
    <div className={cn(styles.grid, styles.grid_bar)}>
      <div />
      <BarBox
        label="repayments"
        value={maybePluralize(transactedRepayments.length, 'repayment')}
        subLabel={maybePluralize(repayments.length, 'repayment')}
      />
      <BarBox
        right
        label="estimated repayments"
        value={
          <Box size="xl" color="turquoizeBright">
            <Space size={6}>
              <BlackboardChartSecondIcon width={16} height={16} />
              <p>
                (<CentsMoney as="span" value={totalEstimatedRepaymentsAmount} />
                )
              </p>
            </Space>
          </Box>
        }
      />
      <BarBox
        right
        label="actual repaid"
        value={formatMoney(totalTransactedRepaymentsAmount, {cents: true})}
        subLabel={`${countPercentage(
          totalTransactedRepaymentsAmount,
          totalAmount
        )}% from loan`}
      />
    </div>
  )
}

const BarBox = ({right, label, value, subLabel}: BarBoxProps) => {
  const contentJSX = (
    <>
      <p className={styles.label}>{label}</p>
      <div className={styles.value}>{value}</div>
      <p className={styles.subLabel}>{subLabel}</p>
    </>
  )

  return (
    <Box
      className={right ? styles.rightBlock : undefined}
      align={right ? 'right' : undefined}
    >
      {right ? <div>{contentJSX}</div> : contentJSX}
    </Box>
  )
}
